import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Download, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function CapabilityStatementModal({ isOpen, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();

  const handleLearnMore = (e) => {
    e.preventDefault();
    onClose();
    navigate('/about');
    // Wait for navigation and then scroll to section
    setTimeout(() => {
      const section = document.getElementById('capability-section');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/capability-statement`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, phone }),
      });

      if (!response.ok) {
        throw new Error("Failed to process request");
      }

      const data = await response.json();
      
      // Trigger download
      const link = document.createElement("a");
      link.href = "/capability_statement.png";
      link.download = "PWC_Capability_Statement.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Download started! Thank you for your interest.");
      
      // Reset form and close modal
      setName("");
      setPhone("");
      onClose();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-sm animate-in fade-in zoom-in-95 duration-300">
        {/* Glow effect behind modal */}
        <div className="absolute -inset-1 bg-gradient-to-b from-[#E63946]/30 to-transparent rounded-3xl blur-xl opacity-60" />
        
        <div className="relative bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-all duration-200"
            data-testid="close-capability-modal"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Icon */}
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E63946] to-[#991B1B] flex items-center justify-center mb-6 shadow-lg shadow-[#E63946]/20">
              <Download className="w-7 h-7 text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-2">
              Get Our Capability Statement!
            </h2>
            <p className="text-[#737373] text-sm mb-8">
              Enter your details below to download
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  id="cap-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Your name"
                  className={`w-full px-4 py-3.5 bg-[#141414] border rounded-xl text-white placeholder-[#525252] focus:outline-none transition-all duration-200 ${
                    focusedField === 'name' 
                      ? 'border-[#E63946] shadow-[0_0_0_3px_rgba(230,57,70,0.1)]' 
                      : 'border-[#1F1F1F] hover:border-[#2A2A2A]'
                  }`}
                  data-testid="capability-name-input"
                  required
                />
              </div>

              <div className="relative">
                <input
                  type="tel"
                  id="cap-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Phone number"
                  className={`w-full px-4 py-3.5 bg-[#141414] border rounded-xl text-white placeholder-[#525252] focus:outline-none transition-all duration-200 ${
                    focusedField === 'phone' 
                      ? 'border-[#E63946] shadow-[0_0_0_3px_rgba(230,57,70,0.1)]' 
                      : 'border-[#1F1F1F] hover:border-[#2A2A2A]'
                  }`}
                  data-testid="capability-phone-input"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-2 bg-[#E63946] hover:bg-[#DC2626] disabled:bg-[#E63946]/50 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 group shadow-lg shadow-[#E63946]/20 hover:shadow-[#E63946]/30"
                data-testid="capability-download-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>Download Now</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleLearnMore}
                className="w-full mt-1 text-[#737373] hover:text-white text-sm font-medium py-2 flex items-center justify-center gap-1.5 transition-colors duration-200"
              >
                Learn More
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
